import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import {
  SidebarIcon,
  UserIcon,
  LayoutGridIcon,
  CreditCardIcon,
  LogOutIcon,
  ChevronUpIcon,
} from './icons';

/**
 * Sidebar — left navigation column of the Electron shell.
 *
 * The account chip doubles as the trigger for a profile dropdown that
 * mirrors the website Header's user menu (Profile, Dashboard, Billing,
 * Log Out). The dropdown is anchored above the chip — same affordance as
 * Linear/Slack/Figma — so it never overlaps the navigation list.
 */
export default function Sidebar({ activePaneKey, onSelectPane, panes, onToggle, onOpenBilling }) {
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const initial = (user?.name || user?.email || 'U').trim().charAt(0).toUpperCase();
  const planLabel = user?.planTier ? user.planTier.toUpperCase() : 'FREE';

  // Plan minutes — shape mirrors what /api/profile returns. We fall back
  // to a 60-min free tier so the meter is always meaningful even before
  // the backend wires up these fields.
  const minutesUsed = Math.max(0, Math.round(user?.minutesUsed || 0));
  const minutesQuota = Math.max(1, Math.round(user?.minutesQuota || 60));
  const pct = Math.min(100, Math.round((minutesUsed / minutesQuota) * 100));
  const meterClass = pct >= 90 ? 'danger' : pct >= 65 ? 'warn' : '';

  // Close on outside click and Escape — both are expected for a popover.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const go = (route) => {
    setMenuOpen(false);
    navigate(route);
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/');
  };

  const handleBilling = () => {
    setMenuOpen(false);
    onOpenBilling?.();
  };

  return (
    <aside className="es-sidebar">
      <div className="es-sidebar-toggle-row">
        <button
          type="button"
          onClick={onToggle}
          title="Hide sidebar"
          aria-label="Hide sidebar"
          className="es-brand-toggle"
        >
          <SidebarIcon size={16} />
        </button>
      </div>

      <div className="es-account-wrap" ref={menuRef}>
        <button
          type="button"
          className={`es-account ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(o => !o)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          title="Account menu"
        >
          <div className="es-avatar">{initial}</div>
          <div className="es-account-meta">
            <div className="es-account-name">{user?.name || user?.email || 'Sign in'}</div>
            <div className="es-account-plan">{planLabel} · DESKTOP</div>
          </div>
          <span className={`es-account-caret ${menuOpen ? 'open' : ''}`} aria-hidden="true">
            <ChevronUpIcon size={14} />
          </span>
        </button>

        {menuOpen && (
          <div className="es-account-menu" role="menu">
            <div className="es-account-menu-head">
              <div className="es-account-menu-name">{user?.name || 'Member'}</div>
              {user?.email && (
                <div className="es-account-menu-email">{user.email}</div>
              )}
            </div>
            <div className="es-account-menu-divider" />
            <button type="button" role="menuitem" className="es-account-menu-item" onClick={() => go('/profile')}>
              <UserIcon size={15} />
              <span>Profile</span>
            </button>
            <button type="button" role="menuitem" className="es-account-menu-item" onClick={() => go('/Dashboard')}>
              <LayoutGridIcon size={15} />
              <span>Dashboard</span>
            </button>
            <button type="button" role="menuitem" className="es-account-menu-item" onClick={handleBilling}>
              <CreditCardIcon size={15} />
              <span>Billing &amp; Subscription</span>
            </button>
            <div className="es-account-menu-divider" />
            <button type="button" role="menuitem" className="es-account-menu-item danger" onClick={handleLogout}>
              <LogOutIcon size={15} />
              <span>Log out</span>
            </button>
          </div>
        )}
      </div>

      <div className="es-usage" title={`${minutesUsed} of ${minutesQuota} minutes used this period`}>
        <div className="es-usage-row">
          <span className="es-usage-label">Usage</span>
          <span className="es-usage-value">{minutesUsed}m / {minutesQuota}m</span>
        </div>
        <div className="es-usage-track">
          <div className={`es-usage-fill ${meterClass}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="es-nav-label">Workspace</div>
      <nav className="es-nav">
        {panes.map(p => {
          const Icon = p.Icon;
          const isActive = activePaneKey === p.key;
          return (
            <button
              key={p.key}
              type="button"
              className={`es-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => onSelectPane(p.key)}
            >
              <span className="es-nav-icon">
                {Icon ? <Icon size={16} /> : null}
              </span>
              <span>{p.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="es-sidebar-footer">v1.0.0 · Desktop</div>
    </aside>
  );
}
